import { Component, inject, Inject } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IContactWidget, IWidgetHeaderOptions } from '../interfaces/contact-widget.interface';
import { MatSelectChange } from '@angular/material/select';
import { ModelService, PluginInjectorWidget, PluginInjectorService } from 'core-client/api';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'lib-contact-widget-settings',
	imports: [
		SharedModule
	],
  templateUrl: './contact-widget-settings.component.html',
  styleUrl: './contact-widget-settings.component.scss'
})
export class ContactWidgetSettingsComponent {
	// Services
	protected readonly translate: TranslateService = inject(TranslateService);
	protected readonly dialogRef: MatDialogRef<ContactWidgetSettingsComponent, any> =
		inject(MatDialogRef<ContactWidgetSettingsComponent>);
	protected readonly modelService: ModelService = inject(ModelService);
	private readonly snackBar = inject(MatSnackBar);
	protected readonly pluginInjectorService: PluginInjectorService = inject(PluginInjectorService);

	// Variables
	protected contactWidget: IContactWidget;
	protected selectedWidgetHeaderOption: string = 'alwaysDisplayed';
	protected widgetTitle: string;
	protected showWidgetTitle: boolean = false;
	protected widgetId: string;

	constructor(@Inject(MAT_DIALOG_DATA) protected readonly data: PluginInjectorWidget.IWidget) {
		this.contactWidget = data.appPluginWidget;
		this.widgetId = data.appPluginWidget._id;
		this.widgetTitle = data.appPluginWidget.name;
		this.showWidgetTitle = data.appPluginWidget.settings.showWidgetTitle;
		const widgetHeaderOption = data.appPluginWidget.settings.widgetHeader;
		const widgetHeaderOptions = Object.keys(widgetHeaderOption) as IWidgetHeaderOptions[];
		this.selectedWidgetHeaderOption = widgetHeaderOptions.filter(
			(key: IWidgetHeaderOptions) => widgetHeaderOption[key]
		)[0];
	}

	protected selectHeaderOption($event: MatSelectChange): void {
		const headerOptions: Record<string, { alwaysDisplayed: boolean; onHoverDisplayed: boolean; alwaysOff: boolean; transparent: boolean }> = {
			alwaysDisplayed: { alwaysDisplayed: true, onHoverDisplayed: false, alwaysOff: false, transparent: false },
			onHoverDisplayed: { alwaysDisplayed: false, onHoverDisplayed: true, alwaysOff: false, transparent: false },
			alwaysOff: { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: true, transparent: false },
			transparent: { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: false, transparent: true }
		};

		if ($event.value in headerOptions) {
			this.contactWidget.settings.widgetHeader = headerOptions[$event.value];
		}
	}

	protected getText(key: unknown): string {
		const translations: Record<string, string> = {
			alwaysDisplayed: 'ASTIMAX.CONTACT.WIDGET.WIDGET_HEADER.ALWAYS_DISPLAYED',
			onHoverDisplayed: 'ASTIMAX.CONTACT.WIDGET.WIDGET_HEADER.ON_HOVER_DISPLAYED',
			alwaysOff: 'ASTIMAX.CONTACT.WIDGET.WIDGET_HEADER.ALWAYS_OFF',
			transparent: 'ASTIMAX.CONTACT.WIDGET.WIDGET_HEADER.TRANSPARENT'
		};

		return this.translate.instant(translations[key as string] || 'unknown');
	}

	async onSubmit(): Promise<void> {
		if (!this.contactWidget) {
			return;
		}

		// update variables
		this.contactWidget.name = this.widgetTitle;
		this.contactWidget.settings.showWidgetTitle = this.showWidgetTitle;

		// save changes to database
		await this.modelService._appPluginWidget.upsertOne(this.contactWidget);

		// Update widget name
		// Directly data-binding changes
		const widget = this.pluginInjectorService.widgetService.getWidgetItem(this.contactWidget._id);
		if (widget) {
			widget.name = this.contactWidget.name;
		}

		this.snackBar.open(
			this.translate.instant('ASTIMAX.CONTACT.WIDGET.SETTINGS_CHANGES_UPDATED_SUCCESSFULLY'),
			'Close',
			{ duration: 3000 }
		);

		this.dialogRef.close();
	}
}
